import React, { useMemo } from 'react';
import snapshotsData from '../../data/sim/snapshots.json';

const SNAPSHOTS = snapshotsData.snapshots || [];

export function PredictionDelta({ result }) {
  const movers = useMemo(() => {
    if (SNAPSHOTS.length < 2) return null;
    const prev = SNAPSHOTS[SNAPSHOTS.length - 2];
    const curr = SNAPSHOTS[SNAPSHOTS.length - 1];
    const teamLookup = new Map(result.teams.map((t) => [t.id, t]));
    const rows = [];
    for (const [id, p] of Object.entries(curr.probabilities)) {
      const before = prev.probabilities[id]?.champ ?? 0;
      const after = p.champ;
      const delta = after - before;
      if (Math.abs(delta) < 0.005) continue;
      const team = teamLookup.get(id);
      if (!team) continue;
      rows.push({ id, name: team.name_fr || team.name_en || team.name, flag: team.flag, before, after, delta });
    }
    rows.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    return { prev, curr, rows: rows.slice(0, 10) };
  }, [result]);

  if (!movers) return null;

  return (
    <div className="card-elev p-5 sm:p-6 mb-8 border border-[#2A4A6B]">
      <header className="mb-4">
        <div className="font-mono text-[10px] uppercase tracking-wider text-[#00C853] font-bold">
          Dernières Évolutions
        </div>
        <h3 className="mt-1 font-heading text-2xl font-bold text-white tracking-tight">
          Évolution des Probabilités de Titre
        </h3>
        <p className="text-xs text-gray-400 mt-2">
          Comparé aux prévisions du{' '}
          <span className="text-white">
            {new Date(movers.prev.id).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>{' '}
          · Événement déclencheur : <strong className="text-white">{movers.curr.trigger}</strong> ({movers.curr.decided_matches} match{movers.curr.decided_matches > 1 ? 's' : ''} joué{movers.curr.decided_matches > 1 ? 's' : ''}).
        </p>
      </header>

      <div className="overflow-x-auto rounded-xl border border-[#2A4A6B]/50 bg-[#1E3A5F]/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#2A4A6B]/50 bg-[#1E3A5F]/20 font-mono text-[10px] uppercase tracking-wider text-gray-400">
              <th className="px-4 py-3 text-left">Équipe</th>
              <th className="px-4 py-3 text-right">Avant</th>
              <th className="px-4 py-3 text-right">Après</th>
              <th className="px-4 py-3 text-right">Écart</th>
            </tr>
          </thead>
          <tbody>
            {movers.rows.map((r) => {
              const positive = r.delta > 0;
              return (
                <tr
                  key={r.id}
                  className="border-b border-[#2A4A6B]/30 hover:bg-[#1E3A5F]/20 transition-colors"
                >
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <img 
                        src={r.flag ? (r.flag.startsWith('http') ? r.flag : `https://flagcdn.com/w80/${r.flag.toLowerCase()}.png`) : `https://flagcdn.com/w80/un.png`} 
                        alt="" 
                        className="w-5 h-3.5 object-cover rounded shadow-sm shrink-0" 
                      />
                      <span className="font-semibold text-white text-xs sm:text-sm">{r.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs tabular-nums text-gray-400">
                    {(r.before * 100).toFixed(1)}%
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs tabular-nums text-white">
                    {(r.after * 100).toFixed(1)}%
                  </td>
                  <td className={`px-4 py-2.5 text-right font-mono text-xs tabular-nums font-bold ${positive ? 'text-[#00C853]' : 'text-red-400'}`}>
                    {positive ? '+' : ''}{(r.delta * 100).toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
